import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { cn } from '../../lib/utils';
import { Button, buttonVariants } from './button';

/**
 * shadcn's Calendar, on react-day-picker.
 *
 * Replaces `<input type="date">`, which renders whatever calendar the browser feels like — a
 * different one per browser and platform, none of them matching the rest of the admin, and none
 * of them theme-aware. This one is ours in both modes.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaults = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn('bg-background group/calendar p-3', className)}
      classNames={{
        root: cn('w-fit', defaults.root),
        months: cn('flex gap-4 flex-col md:flex-row relative', defaults.months),
        month: cn('flex flex-col w-full gap-4', defaults.month),
        nav: cn(
          'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
          defaults.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 aria-disabled:opacity-50 p-0 select-none',
          defaults.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 aria-disabled:opacity-50 p-0 select-none',
          defaults.button_next,
        ),
        month_caption: cn(
          'flex items-center justify-center h-8 w-full px-8',
          defaults.month_caption,
        ),
        caption_label: cn('select-none font-medium text-sm', defaults.caption_label),
        month_grid: 'w-full border-collapse',
        weekdays: cn('flex', defaults.weekdays),
        weekday: cn(
          'text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none',
          defaults.weekday,
        ),
        week: cn('flex w-full mt-2', defaults.week),
        day: cn(
          'relative w-full h-full p-0 text-center group/day aspect-square select-none',
          defaults.day,
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 p-0 font-normal aria-selected:opacity-100',
          defaults.day_button,
        ),
        selected: cn(
          'bg-primary text-primary-foreground rounded-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button:hover]:bg-primary [&>button:hover]:text-primary-foreground',
          defaults.selected,
        ),
        today: cn('bg-accent text-accent-foreground rounded-md', defaults.today),
        outside: cn('text-muted-foreground aria-selected:text-muted-foreground', defaults.outside),
        disabled: cn('text-muted-foreground opacity-50', defaults.disabled),
        hidden: cn('invisible', defaults.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ className: chevronClass, orientation, ...rest }) =>
          orientation === 'left' ? (
            <ChevronLeftIcon className={cn('size-4', chevronClass)} {...rest} />
          ) : (
            <ChevronRightIcon className={cn('size-4', chevronClass)} {...rest} />
          ),
        ...components,
      }}
      {...props}
    />
  );
}

export { Calendar, Button as CalendarButton };
